/**
 * Shared Groq→template weekly report pipeline.
 * Used by WeeklyReportPanel (browser) and Cloud Function softcopy job.
 */

import type { TelemetryRow } from '@/types';
import type { SheetSource } from '@/config/sheets';
import type { StateTariff } from '@/tariffs';
import {
  buildWeeklyReportPayload,
  type WeeklyReportPayload,
} from '@/lib/reports/buildWeeklyReportPayload';
import { renderReportSentences } from '@/lib/reports/renderReportSentences';
import { narrateSection } from '@/lib/reports/narrateSection';
import { formatRupees, formatPF, formatPct, formatNumber } from '@/lib/formatters';
import type { DateWindow } from '@/lib/reports/weekWindow';
import type {
  RenderedHealthForPdf,
  RenderedSectionForPdf,
} from '@/components/dashboard/WeeklyReportPdfDocument';

/**
 * Per-finding-kind narration structure — every number here comes from
 * `h.stats` (computed in buildWeeklyReportPayload.ts), the LLM only phrases
 * it. Kinds not listed here (rare in practice — see scoring in
 * buildHealthFindings) fall back to a short generic structure.
 */
const HEALTH_STRUCTURE_HINTS: Record<string, string[]> = {
  neutralCurrent: [
    'State the mean and peak neutral current during active operation in one sentence (e.g. "Neutral current averaged X A with a peak of Y A during active operation this week").',
    'State plainly that neutral current exceeded the normal threshold of [threshold] A in [X]% of readings, using the exact given values — lead with this figure.',
    'Then state the percentage of readings where neutral current exceeded 10% of the average phase current, as a secondary/supporting figure.',
    'Add one closing sentence using the FACTS: in a balanced, clean 3-phase system neutral current should be near zero, so this points to unbalanced single-phase loads or harmonic content.',
  ],
  currentUnbalance: [
    'State each phase\'s current at the worst reading this week — Phase R, Phase Y, and Phase B — and the average current across the three phases, all in one clause (e.g. "Phase R carried X A, Phase Y carried Y A, and Phase B carried Z A, averaging W A across the three phases").',
    'State the imbalance percentage at that reading and whether it exceeds the stated threshold (use the FACT to know which — never guess), naming the threshold value.',
    'State which single phase contributed most to that imbalance.',
    'Use the matching FACT to add one sentence about how confident to be in this: if the FACT says it was the single most extreme reading of the week with nothing else close to it, say this looks like an isolated reading worth verifying rather than a confirmed pattern — regardless of what power showed. Otherwise, if the FACT says power moved correspondingly, briefly note this looks like a real event (one short clause); if it says power did NOT move correspondingly, clearly flag this as a possible sensor reading issue to verify before assuming a real fault.',
    'If a FACT about harmonic correlation is given, weave it in as one closing sentence on what this combination suggests (non-linear loads unevenly distributed across phases). If no such FACT is given, skip this step entirely.',
  ],
  lowPF: [
    'State which phase is most affected, its mean power factor, and the percentage of operating readings where it was below the stated threshold, over how many days. Use the matching FACT to decide the wording: if the FACT says the average is genuinely below threshold, you may call it "chronically low"; if the FACT says the average stayed ABOVE threshold despite dipping sometimes, do NOT use the word "chronic" or "chronically" — say it "dipped below threshold sometimes" or similar instead.',
    'State the mean power factor of the phase(s) that stayed fine, for contrast (name each) — use only the numbers labeled as comparison/fine phases.',
    'If a second-affected-phase number is given, state it as its own real finding in one additional sentence (percentage below threshold and mean PF for that second phase) — apply the same chronic-vs-dips wording rule using its matching FACT. Do NOT describe it as merely "higher than the worst phase," treat it as a genuine second finding.',
    'Add one closing sentence using the FACTS on what this pattern suggests physically about where the reactive load is concentrated — mention both affected phases if two are given.',
  ],
  phaseLoss: [
    'State the number of confirmed phase-loss events this week and the voltage level below which a phase counts as lost.',
    'State the highest active load recorded during one of these events.',
    'Add one closing sentence using the FACTS clarifying that this happened while the plant was actively drawing load, not while idle.',
  ],
  voltageUnbalance: [
    'State each phase\'s voltage at the worst reading this week — Phase R, Phase Y, and Phase B — and the average voltage across the three phases, all in one clause.',
    'State the imbalance percentage at that reading and whether it exceeds the stated threshold, naming the threshold value, and which single phase deviated most.',
    'State the mean imbalance percentage across the whole week and the percentage of readings that exceeded the threshold.',
    'Add one closing sentence using the FACTS on why sustained voltage imbalance matters physically (motor heating) and that it usually originates on the supply side, not from equipment inside the plant.',
  ],
  highVoltageTHD: [
    'State which phase has the highest voltage harmonic distortion, its mean and peak THD percentage this week, and the percentage of readings where it exceeded the stated threshold, over how many days.',
    'State the mean THD of the other two phases for comparison, naming each.',
    'Add one closing sentence using the FACTS on what elevated voltage harmonic distortion physically causes.',
  ],
  highCurrentTHD: [
    'State which phase has the highest current harmonic distortion, its mean and peak THD percentage this week, and the percentage of readings where it exceeded the stated threshold, over how many days.',
    'State the mean THD of the other two phases for comparison, naming each.',
    'Add one closing sentence using the FACTS on what elevated current harmonic distortion physically causes and what kind of load typically causes it.',
  ],
  overload: [
    'State the average and peak load as a percentage of rated capacity this week.',
    'State the overload threshold used, the percentage of readings that exceeded it, and how many days this spanned.',
    'Add one closing sentence using the FACTS on the risk of running close to or above rated capacity.',
  ],
  frequency: [
    'State the mean and peak grid frequency this week, and the largest deviation from the 50 Hz nominal, naming which direction (above or below 50 Hz) the largest deviation was.',
    'State the deviation threshold used and the percentage of readings that exceeded it.',
    'Add one closing sentence using the FACTS that this is typically a grid-side condition, not something caused on-site.',
  ],
};
const GENERIC_HEALTH_STRUCTURE = [
  'State the percentage of readings this was detected in and the peak value reached this week, in one sentence.',
  'State the engineering threshold this is being compared against, and briefly note how far the peak value is above it (e.g. "well above" vs "just above") — never invent a number, use only what is given.',
  'If a FACT is given explaining the physical implication of this, add it as one short closing sentence, in plain non-technical language.',
];

/** Section shape for PDF + panel (panel may use `source`). */
export interface PipelineRenderedSection extends RenderedSectionForPdf {
  source: 'llm' | 'fallback';
}

export type PipelineRenderedHealth = RenderedHealthForPdf;

export interface WeeklyReportPipelineResult {
  payload: WeeklyReportPayload;
  sections: PipelineRenderedSection[];
  health: PipelineRenderedHealth[];
}

export interface WeeklyReportPipelineParams {
  allRows: TelemetryRow[];
  sheet: SheetSource;
  tariff: StateTariff;
  contractDemandKva: number;
  reportWeek: DateWindow;
  comparisonWeek: DateWindow;
  sectionTitle?: (en: string) => string;
}

export async function runWeeklyReportPipeline(
  params: WeeklyReportPipelineParams,
): Promise<WeeklyReportPipelineResult> {
  const {
    allRows,
    sheet,
    tariff,
    contractDemandKva,
    reportWeek,
    comparisonWeek,
    sectionTitle,
  } = params;

  const title = (en: string) => sectionTitle?.(en) ?? en;

  const computedPayload = buildWeeklyReportPayload({
    allRows,
    sheet,
    tariff,
    contractDemandKva,
    reportWeek,
    comparisonWeek,
  });

  // Deterministic fallback sentences (always available, guaranteed correct).
  const templated = renderReportSentences(computedPayload);

  // ── PF section ──────────────────────────────────────────────────────
  // Only surface the "could have saved more" delta when the week's actual
  // outcome was a surcharge (a real cost) — if we already earned a rebate,
  // stating a hypothetical bigger rebate on top just confuses the message.
  const pf = computedPayload.billing.pf;
  const pfWasSurcharge = pf.actualImpactRs < 0;
  const pfWasZero = pf.actualImpactRs === 0;
  const pfDipIsMeaningful = pf.dipIsMeaningful;
  const pfResult = !pf.applicable
    // This tariff has no per-period PF rebate/surcharge table at all — say so
    // explicitly rather than letting the LLM describe it as a "net zero" week,
    // which would wrongly imply PF rebates/surcharges exist but happened to cancel out.
    ? await narrateSection({
        section: 'Power Factor',
        allowedValues: {
          'average power factor this week': formatPF(pf.weekAvgPf),
          ...(pfDipIsMeaningful ? { 'lowest power factor reached this week': formatPF(pf.weekLowPf) } : {}),
        },
        facts: [
          'This tariff does not apply a power factor rebate or surcharge at all — there is no rupee impact to report for power factor, regardless of the level it reached.',
          pfDipIsMeaningful
            ? 'Power factor dipped meaningfully below its own average at some point this week — mention the low point.'
            : 'Power factor stayed steady all week, close to its average — do NOT mention a separate "low point" number, there was no meaningful dip.',
        ],
        structureHint: [
          pfDipIsMeaningful
            ? 'State the average power factor this week and the lowest point reached.'
            : 'State that power factor stayed steady around its average value this week — do not invent or mention a separate low point.',
          'State plainly that this tariff does not apply a power factor rebate or surcharge, so there is no cost or saving to report on this metric.',
        ],
        fallback: templated.pf.lines.join(' '),
      })
    : await narrateSection({
    section: 'Power Factor',
    allowedValues: {
      'average power factor this week': formatPF(pf.weekAvgPf),
      ...(pfDipIsMeaningful ? { 'lowest power factor reached this week': formatPF(pf.weekLowPf) } : {}),
      'number of days power factor fell below the rebate threshold': String(pf.daysBelowThreshold),
      'total number of days with data this week (use this as the "out of" denominator, never assume 7)': String(pf.daysSpan),
      'the rebate-eligible power factor threshold for this tariff': formatPF(pf.thresholdUsed),
      ...(pfWasZero
        ? {}
        : {
            [pfWasSurcharge
              ? 'surcharge (extra cost) paid on the bill this week due to low power factor'
              : 'rebate earned on the bill this week thanks to good power factor']: formatRupees(Math.abs(pf.actualImpactRs)),
          }),
      ...(pfWasSurcharge
        ? { 'rupees that could have been saved this week if power factor had stayed near ideal instead of causing a surcharge': formatRupees(Math.abs(pf.deltaRs)) }
        : {}),
    },
    facts: [
      pfDipIsMeaningful
        ? 'Power factor dipped meaningfully below its own average at some point this week — mention the low point.'
        : 'Power factor stayed steady all week, close to its average — do NOT mention a separate "low point" number, there was no meaningful dip.',
      pfWasSurcharge
        ? 'The net effect this week was a surcharge (a real cost) — mention what could have been saved if PF had stayed near ideal.'
        : pfWasZero
          ? 'The net effect this week was neither a rebate nor a surcharge (net zero) — just state that plainly, do not mention any hypothetical rebate amount.'
          : 'The net effect this week was a rebate (a saving) — do NOT mention any hypothetical bigger rebate, just report the rebate earned.',
    ],
    structureHint: [
      pfDipIsMeaningful
        ? 'State the average power factor this week and the lowest point reached.'
        : 'State that power factor stayed steady around its average value this week — do not invent or mention a separate low point.',
      'State the number of days it fell below the rebate threshold, out of the total days with data this week (e.g. "on 1 of 6 days") — if the count below threshold is 0, say "no days" instead of writing the digit 0. Never assume the week is 7 days — always use the given total.',
      pfWasSurcharge
        ? 'State the surcharge amount paid on the bill this week due to low power factor.'
        : pfWasZero
          ? 'State plainly that no rebate or surcharge applied on power factor this week.'
          : `State the rebate amount earned on the bill this week, plainly. ${pf.daysBelowThreshold > 0 ? 'You may use a word like "still" here since there was a dip to overcome.' : 'Do NOT use words like "still" or "nonetheless" — there was nothing to overcome this week, so just state the rebate as a plain fact, no implied contrast.'}`,
      pfWasSurcharge
        ? 'Add one final clause: the amount that could have been saved if power factor had stayed near ideal.'
        : '',
    ].filter(Boolean),
    fallback: templated.pf.lines.join(' '),
  });

  // ── CD utilization section ───────────────────────────────────────────
  // Only pass the numbers actually relevant to this week's outcome (exceeded
  // vs under-utilized vs healthy) so the LLM has nothing spare to ramble about.
  const cd = computedPayload.billing.cd;
  const cdResult = await narrateSection({
    section: 'Contract Demand Utilisation',
    allowedValues: {
      'average contract demand utilisation this week (percent)': formatPct(cd.avgUtilizationPct, 0),
      'average demand drawn this week in kVA': formatNumber(cd.avgUtilizationKva, 0),
      'peak contract demand utilisation this week (percent)': formatPct(cd.peakUtilizationPct, 0),
      'peak demand drawn this week in kVA': formatNumber(cd.peakUtilizationKva, 0),
      ...(cd.exceeded
        ? {
            'kVA by which peak demand exceeded the contract demand limit': formatNumber(cd.excessKva, 0),
            'penalty charged in rupees for exceeding contract demand': formatRupees(cd.penaltyRs),
          }
        : {}),
      ...(cd.underUtilized
        ? { 'kVA of contracted capacity left unused even at the week\'s peak': formatNumber(cd.unusedKvaAtPeak, 0) }
        : {}),
    },
    facts: [
      cd.exceeded
        ? 'Peak demand exceeded the contract demand this week — a penalty was charged.'
        : 'Peak demand did not exceed the contract demand this week — no penalty was charged.',
      cd.underUtilized ? 'Capacity was under-used this week — paying for kVA that never got used even at the peak.' : '',
    ].filter(Boolean),
    structureHint: [
      'State the average utilisation percent immediately followed by its kVA value in the same clause, e.g. "X% (Y kVA)".',
      'State the peak utilisation percent immediately followed by its peak kVA value the same way, e.g. "X% (Y kVA)".',
      cd.exceeded
        ? 'State that peak demand went over the contract demand and the penalty amount charged.'
        : 'State plainly that there was no penalty this week, in one short clause — do not explain why in extra words.',
      cd.underUtilized
        ? 'Add one final short clause: that capacity was under-used, naming the unused kVA figure, framed as capacity that could have been used more efficiently to avoid paying for it.'
        : '',
    ].filter(Boolean),
    fallback: templated.cd.lines.join(' '),
  });

  // ── LF section ────────────────────────────────────────────────────────
  // Same rule as PF: only mention the hypothetical "could have saved" delta
  // when the actual outcome was a surcharge, never when it was already a rebate.
  const lf = computedPayload.billing.lf;
  const lfWasSurcharge = lf.actualImpactRs < 0;
  const lfWasZero = lf.actualImpactRs === 0;
  const lfNearFullUtilization = lf.nearFullUtilization;
  const lfDipIsMeaningful = lf.dipIsMeaningful;
  const lfResult = !lf.applicable
    // Same principle as PF: this tariff has no load-factor rebate/surcharge
    // table at all — say so explicitly instead of a misleading "net zero".
    ? await narrateSection({
        section: 'Load Factor',
        allowedValues: {
          'average load factor this week (percent)': formatPct(lf.weekAvgLfPct, 0),
          ...(lfDipIsMeaningful ? { 'lowest load factor reached this week (percent)': formatPct(lf.weekLowLfPct, 0) } : {}),
        },
        facts: [
          'This tariff does not apply a load factor rebate or surcharge at all — there is no rupee impact to report for load factor, regardless of the level it reached.',
          lfDipIsMeaningful
            ? 'Load factor dipped meaningfully below its own average at some point this week — mention the low point.'
            : 'Load factor stayed steady all week, close to its average — do NOT mention a separate "low point" number, there was no meaningful dip.',
        ],
        structureHint: [
          lfDipIsMeaningful
            ? 'State the average load factor this week and the lowest point reached.'
            : 'State that load factor stayed steady around its average value this week — do not invent or mention a separate low point.',
          'State plainly that this tariff does not apply a load factor rebate or surcharge, so there is no cost or saving to report on this metric.',
        ],
        fallback: templated.lf.lines.join(' '),
      })
    : await narrateSection({
    section: 'Load Factor',
    allowedValues: {
      ...(lfNearFullUtilization ? {} : { 'average load factor this week (percent)': formatPct(lf.weekAvgLfPct, 0) }),
      ...(lfDipIsMeaningful ? { 'lowest load factor reached this week (percent)': formatPct(lf.weekLowLfPct, 0) } : {}),
      ...(lfWasZero
        ? {}
        : {
            [lfWasSurcharge
              ? 'surcharge (extra cost) paid on the bill this week due to low load factor'
              : 'rebate/saving earned on the bill this week thanks to good load factor']: formatRupees(Math.abs(lf.actualImpactRs)),
          }),
      ...(lfWasSurcharge
        ? { 'rupees that could have been saved this week if load factor had stayed near ideal instead of causing a surcharge': formatRupees(Math.abs(lf.deltaRs)) }
        : {}),
    },
    facts: [
      lfNearFullUtilization
        ? 'Load was utilized at close to its full potential all week (average and low point are essentially the same, both near 100%) — say plainly that load was well utilized, do NOT describe a "drop" or "low point" since there wasn\'t a meaningful one, and do NOT state the exact percentage figure (e.g. do not say "averaging 100%") — just say it was fully/well utilized.'
        : lfDipIsMeaningful
          ? 'Load factor dipped meaningfully below its own average at some point this week — mention the low point.'
          : 'Load factor stayed steady all week, close to its average — do NOT mention a separate "low point" number, there was no meaningful dip.',
      lfWasSurcharge
        ? 'The net effect this week was a surcharge (a real cost) — mention what could have been saved if load factor had stayed near ideal.'
        : lfWasZero
          ? 'The net effect this week was neither a rebate nor a surcharge (net zero) — just state that plainly, do not mention any hypothetical saving amount.'
          : 'The net effect this week was a rebate/saving — do NOT mention any hypothetical bigger saving, just report what was saved.',
    ],
    structureHint: [
      lfNearFullUtilization
        ? 'State plainly that load was utilized at close to its full potential this week — do NOT state the exact percentage number, and do not mention a separate low point.'
        : lfDipIsMeaningful
          ? 'State the average load factor this week and the lowest point reached.'
          : 'State that load factor stayed steady around its average value this week — do not invent or mention a separate low point.',
      lfWasSurcharge
        ? 'State the surcharge amount paid on the bill this week due to low load factor.'
        : lfWasZero
          ? 'State plainly that no rebate or surcharge applied on load factor this week.'
          : `State the rebate/saving amount earned on the bill this week, plainly. ${lfDipIsMeaningful ? 'You may use a word like "still" here since there was a dip to overcome.' : 'Do NOT use words like "still" or "nonetheless" — there was nothing to overcome this week, so just state it as a plain fact, no implied contrast.'}`,
      lfWasSurcharge
        ? 'Add one final clause: the amount that could have been saved if load factor had stayed near ideal.'
        : '',
    ].filter(Boolean),
    fallback: templated.lf.lines.join(' '),
  });

  // ── Comparison section ──────────────────────────────────────────────
  // Per-metric direction, computed once here (not left for the LLM to guess),
  // so it can contrast "improved here, but worsened there" when the week was mixed.
  const cmp = computedPayload.billing.comparison;
  let cmpResult: { text: string; source: 'llm' | 'fallback' };
  if (!cmp.hasLastWeekData) {
    // No telemetry at all for the prior week — never let the LLM (or the
    // template) fabricate a "from 0% to X%" jump out of missing data.
    cmpResult = { text: templated.comparison.lines.join(' '), source: 'fallback' };
  } else {
    const pfDir = cmp.pfDeltaPts > 0 ? 'improved' : cmp.pfDeltaPts < 0 ? 'worsened' : 'stayed flat';
    const lfDir = cmp.lfDeltaPts > 0 ? 'improved' : cmp.lfDeltaPts < 0 ? 'worsened' : 'stayed flat';
    const cdDir = cmp.cdUtilDeltaPts > 0 ? 'increased' : cmp.cdUtilDeltaPts < 0 ? 'decreased' : 'stayed flat';
    cmpResult = await narrateSection({
      section: 'Week-over-Week Comparison',
      allowedValues: {
        'last week\'s average power factor': formatPF(cmp.lastWeekPf),
        'this week\'s average power factor': formatPF(cmp.thisWeekPf),
        'last week\'s average load factor (percent)': formatPct(cmp.lastWeekLfPct, 0),
        'this week\'s average load factor (percent)': formatPct(cmp.thisWeekLfPct, 0),
        'last week\'s average contract demand utilisation (percent)': formatPct(cmp.lastWeekCdUtilPct, 0),
        'this week\'s average contract demand utilisation (percent)': formatPct(cmp.thisWeekCdUtilPct, 0),
      },
      facts: [
        `Power factor ${pfDir} this week (went from last week's value to this week's value).`,
        `Load factor ${lfDir} this week (went from last week's value to this week's value).`,
        `Contract demand utilisation ${cdDir} this week (went from last week's value to this week's value) — note: for utilisation, "increased" or "decreased" is neutral, not automatically good or bad.`,
        `Overall performance this week ${cmp.overallLabel} versus last week.`,
      ],
      structureHint: [
        `State that average power factor ${pfDir} — went from last week's value to this week's value.`,
        `State that average load factor ${lfDir} — went from last week's value to this week's value. If this direction is different from power factor's, use a contrasting word like "however" or "but" to connect the two clauses (e.g. "power factor improved... however, load factor worsened...").`,
        `State that average contract demand utilisation ${cdDir} — went from last week's value to this week's value.`,
        'End with one short clause on whether overall performance improved, declined, or was mixed versus last week.',
        'Do NOT mention any rebate, surcharge, or rupee amounts — that comparison is written separately by the app from the billing sections above.',
      ],
      fallback: templated.comparison.lines.join(' '),
    });
  }

  const sections: PipelineRenderedSection[] = [
    { title: title('Power Factor'), text: pfResult.text, tone: templated.pf.tone, source: pfResult.source },
    { title: title('Contract Demand Utilisation'), text: cdResult.text, tone: templated.cd.tone, source: cdResult.source },
    { title: title('Load Factor'), text: lfResult.text, tone: templated.lf.tone, source: lfResult.source },
    {
      title: title('Week-over-Week Comparison'),
      text: cmpResult.text,
      tone: templated.comparison.tone,
      source: cmpResult.source,
      impactTrend: templated.comparison.impactTrend ?? undefined,
    },
  ];

  // Health findings: every number (mean/peak/percentages/phase breakdowns) is
  // already computed in buildHealthFindings — the LLM only narrates FROM those
  // numbers, per HEALTH_STRUCTURE_HINTS below, never inventing anything itself.
  const health = await Promise.all(
    computedPayload.healthFindings.map(async (h) => {
      const result = await narrateSection({
        section: h.title,
        allowedValues: h.stats,
        facts: h.narrativeFacts,
        structureHint: HEALTH_STRUCTURE_HINTS[h.kind] ?? GENERIC_HEALTH_STRUCTURE,
        fallback: h.explanation,
      });
      // Same principle for the recommended action: the ACTUAL recommendation
      // (which equipment, which phase, what to check) is decided here in code,
      // never by the LLM — that content is engineering-reviewed and must stay
      // fixed. But handing it over as a rigid, always-identical sentence read
      // like a stock template disconnected from the specific finding above it.
      // So the LLM is only allowed to rephrase THIS EXACT recommendation so it
      // reads as a direct response to the explanation just given — it may not
      // add a new step, a new cause, or any number not already in `allowedValues`.
      const actionResult = await narrateSection({
        section: `${h.title} — recommended action`,
        allowedValues: h.stats,
        facts: [
          `The only approved recommended action for this specific finding is: "${h.action}"`,
          'This action was already chosen specifically for this problem (which phase, which equipment, why) — it is not a generic stock recommendation.',
        ],
        structureHint: [
          'Rephrase the approved recommended action from the FACTS in your own natural words, in 1-2 sentences, so it reads as a direct response to the explanation just given above it — not a disconnected generic tip.',
          'Do NOT add any new step, piece of equipment, cause, or number that is not already in the approved action — you may only reorder or reword what is given.',
          'Keep it calm and practical — a corrective step to take, not a warning.',
        ],
        fallback: h.action,
      });
      return { title: h.title, explanation: result.text, action: actionResult.text };
    }),
  );

  return { payload: computedPayload, sections, health };
}
